export type PresentationAspectRatio = "16:9" | "9:16" | "1:1";

export interface PresentationVideoInput {
  readonly projectName: string;
  readonly imageBase64: string;
  readonly imageMimeType: string;
  readonly aspectRatio: PresentationAspectRatio;
  readonly logoSource?: string;
  readonly creatorLine?: string;
  readonly onProgress?: (percent: number) => void;
}

export interface PresentationVideoResult {
  readonly blob: Blob;
  readonly mimeType: string;
  readonly extension: "mp4" | "webm";
  readonly width: number;
  readonly height: number;
  readonly durationSeconds: number;
}

const FPS = 24;
const DURATION_SECONDS = 15;

const PROFILES: Record<PresentationAspectRatio, { width: number; height: number }> = {
  "16:9": { width: 3840, height: 2160 },
  "9:16": { width: 2160, height: 3840 },
  "1:1": { width: 2160, height: 2160 }
};

const MEDIA_TYPES = [
  { mimeType: 'video/mp4;codecs="avc1.42E01E,mp4a.40.2"', extension: "mp4" as const },
  { mimeType: "video/webm;codecs=vp8,opus", extension: "webm" as const },
  { mimeType: "video/webm;codecs=vp9,opus", extension: "webm" as const },
  { mimeType: "video/webm", extension: "webm" as const }
];

function clamp(value: number, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value));
}

function smooth(start: number, end: number, value: number) {
  const t = clamp((value - start) / (end - start));
  return t * t * (3 - 2 * t);
}

function fadeWindow(time: number, start: number, end: number, edge = 0.55) {
  return smooth(start, start + edge, time) * (1 - smooth(end - edge, end, time));
}

function roundRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.arcTo(x + width, y, x + width, y + height, safeRadius);
  context.arcTo(x + width, y + height, x, y + height, safeRadius);
  context.arcTo(x, y + height, x, y, safeRadius);
  context.arcTo(x, y, x + width, y, safeRadius);
  context.closePath();
}

function drawImageCover(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number,
  zoom = 1,
  offsetX = 0,
  offsetY = 0
) {
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight) * zoom;
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;
  context.drawImage(
    image,
    (width - drawWidth) / 2 + offsetX,
    (height - drawHeight) / 2 + offsetY,
    drawWidth,
    drawHeight
  );
}

function pickMediaType() {
  if (typeof MediaRecorder === "undefined") {
    throw new Error("Este navegador todavía no permite crear vídeos. Usa Chrome o Edge actualizado.");
  }
  return MEDIA_TYPES.find(({ mimeType }) => MediaRecorder.isTypeSupported(mimeType)) ?? null;
}

function loadImage(source: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("No se pudo abrir la imagen de campaña."));
    image.src = source;
  });
}

function addAmbientAudio(stream: MediaStream) {
  if (typeof AudioContext === "undefined") return null;
  const audioContext = new AudioContext({ sampleRate: 48_000 });
  const destination = audioContext.createMediaStreamDestination();
  const master = audioContext.createGain();
  master.gain.value = 0.032;
  master.connect(destination);
  const start = audioContext.currentTime;

  [55, 82.41, 110].forEach((frequency, index) => {
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    oscillator.type = index === 0 ? "sine" : "triangle";
    oscillator.frequency.value = frequency;
    gain.gain.value = index === 0 ? 0.7 : 0.16;
    oscillator.connect(gain).connect(master);
    oscillator.start(start);
    oscillator.stop(start + DURATION_SECONDS + 0.25);
  });

  [0.2, 3.8, 7.4, 11.2].forEach((offset, index) => {
    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    const frequencies = [220, 277.18, 329.63, 440];
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(frequencies[index], start + offset);
    oscillator.frequency.exponentialRampToValueAtTime(frequencies[index] * 1.5, start + offset + 1.5);
    gain.gain.setValueAtTime(0.0001, start + offset);
    gain.gain.exponentialRampToValueAtTime(0.42, start + offset + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 1.65);
    oscillator.connect(gain).connect(master);
    oscillator.start(start + offset);
    oscillator.stop(start + offset + 1.7);
  });

  const track = destination.stream.getAudioTracks()[0];
  if (track) stream.addTrack(track);
  void audioContext.resume();
  return audioContext;
}

export async function renderPresentationVideo4K(
  input: PresentationVideoInput
): Promise<PresentationVideoResult> {
  const mediaType = pickMediaType();
  if (!mediaType) {
    throw new Error("El navegador no ofrece un formato de vídeo compatible.");
  }
  const profile = PROFILES[input.aspectRatio];
  const canvas = document.createElement("canvas");
  canvas.width = profile.width;
  canvas.height = profile.height;
  const context = canvas.getContext("2d", { alpha: false });
  if (!context || typeof canvas.captureStream !== "function") {
    throw new Error("El navegador no puede renderizar vídeo 4K. Usa Chrome o Edge actualizado.");
  }

  const image = await loadImage(`data:${input.imageMimeType};base64,${input.imageBase64}`);
  const logo = await loadImage(input.logoSource ?? "/assets/omnisynia-logo-official.png");
  const stream = canvas.captureStream(FPS);
  let audioContext: AudioContext | null = null;
  try {
    audioContext = addAmbientAudio(stream);
  } catch {
    audioContext = null;
  }

  const recorder = new MediaRecorder(stream, {
    mimeType: mediaType.mimeType,
    videoBitsPerSecond: 14_000_000,
    audioBitsPerSecond: 128_000
  });
  const chunks: Blob[] = [];
  const complete = new Promise<Blob>((resolve, reject) => {
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    recorder.onerror = () => reject(new Error("El renderizador no pudo terminar el vídeo."));
    recorder.onstop = () => resolve(new Blob(chunks, { type: mediaType.mimeType }));
  });

  const { width, height } = profile;
  const unit = Math.min(width, height);
  const cyan = "#55f3ff";
  const violet = "#8d62ff";
  const white = "#f6fbff";
  const muted = "#a9bdd4";
  const projectName = input.projectName.trim() || "Proyecto OMNISYNIA";
  const creatorLine = input.creatorLine ?? "Michel · Creador   |   Luis Puebla Berlanga · Socio";

  function background(time: number) {
    const gradient = context!.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#01030a");
    gradient.addColorStop(0.48, "#071127");
    gradient.addColorStop(1, "#020713");
    context!.fillStyle = gradient;
    context!.fillRect(0, 0, width, height);
    const x = width * (0.2 + Math.sin(time * 0.22) * 0.035);
    const y = height * (0.35 + Math.cos(time * 0.18) * 0.025);
    const glow = context!.createRadialGradient(x, y, 0, x, y, unit * 0.58);
    glow.addColorStop(0, "rgba(141,98,255,0.28)");
    glow.addColorStop(1, "rgba(141,98,255,0)");
    context!.fillStyle = glow;
    context!.fillRect(0, 0, width, height);
  }

  function centeredText(text: string, y: number, size: number, color: string, alpha: number, weight = 700) {
    context!.save();
    context!.globalAlpha = alpha;
    context!.fillStyle = color;
    context!.font = `${weight} ${size}px "Segoe UI", Arial, sans-serif`;
    context!.textAlign = "center";
    context!.textBaseline = "middle";
    context!.fillText(text, width / 2, y, width * 0.9);
    context!.restore();
  }

  function officialLogo(y: number, size: number, alpha: number) {
    context!.save();
    context!.globalAlpha = alpha;
    context!.shadowColor = "rgba(85,243,255,0.48)";
    context!.shadowBlur = unit * 0.025;
    context!.drawImage(logo, width / 2 - size / 2, y - size / 2, size, size);
    context!.restore();
  }

  function drawIntro(time: number) {
    const alpha = fadeWindow(time, 0, 3.7);
    background(time);
    const reveal = smooth(0.1, 1.1, time);
    officialLogo(height * 0.22, unit * 0.2, alpha * reveal);
    context!.save();
    context!.globalAlpha = alpha * reveal;
    context!.shadowColor = "rgba(85,243,255,0.55)";
    context!.shadowBlur = unit * 0.035;
    centeredText("OMNISYNIA", height * 0.43, unit * 0.105, white, 1);
    context!.restore();
    centeredText("DE UNA IDEA A UNA INTELIGENCIA", height * 0.56, unit * 0.032, cyan, alpha * smooth(0.65, 1.45, time), 600);
    centeredText(projectName, height * 0.65, unit * 0.024, muted, alpha * smooth(1.05, 1.85, time), 500);
  }

  function drawCampaign(time: number) {
    const alpha = fadeWindow(time, 3.15, 7.9);
    context!.save();
    context!.globalAlpha = alpha;
    const zoom = 1.02 + (time - 3.1) * 0.008;
    drawImageCover(context!, image, width, height, zoom, Math.sin(time * 0.45) * unit * 0.012);
    const shade = context!.createLinearGradient(0, 0, 0, height);
    shade.addColorStop(0, "rgba(0,4,14,0.08)");
    shade.addColorStop(1, "rgba(0,4,14,0.38)");
    context!.fillStyle = shade;
    context!.fillRect(0, 0, width, height);
    context!.restore();
    centeredText("IMAGEN PUBLICITARIA · 4K REAL", height * 0.9, unit * 0.026, cyan, alpha * smooth(3.7, 4.4, time), 700);
  }

  function drawCapabilities(time: number) {
    const alpha = fadeWindow(time, 7.25, 11.85);
    background(time);
    centeredText("UNA APP. MÚLTIPLES CAPACIDADES.", height * 0.22, unit * 0.047, white, alpha);
    const items = ["CONECTA", "SINCRONIZA", "TRANSFORMA"];
    const horizontal = width >= height;
    items.forEach((label, index) => {
      const local = smooth(7.65 + index * 0.26, 8.35 + index * 0.26, time);
      const cardWidth = horizontal ? width * 0.25 : width * 0.72;
      const cardHeight = horizontal ? height * 0.38 : height * 0.18;
      const x = horizontal ? width * (0.09 + index * 0.295) : width * 0.14;
      const y = horizontal ? height * 0.39 : height * (0.34 + index * 0.205);
      context!.save();
      context!.globalAlpha = alpha * local;
      roundRect(context!, x, y, cardWidth, cardHeight, unit * 0.025);
      context!.fillStyle = "rgba(8,18,39,0.82)";
      context!.fill();
      context!.strokeStyle = index === 1 ? violet : cyan;
      context!.lineWidth = Math.max(3, unit * 0.002);
      context!.stroke();
      centeredText(String(index + 1).padStart(2, "0"), y + cardHeight * 0.31, unit * 0.035, index === 1 ? violet : cyan, alpha * local, 800);
      centeredText(label, y + cardHeight * 0.61, unit * 0.035, white, alpha * local, 700);
      context!.restore();
    });
  }

  function drawFinal(time: number) {
    const alpha = smooth(11.2, 12.1, time);
    context!.save();
    context!.globalAlpha = alpha;
    drawImageCover(context!, image, width, height, 1.08 + (time - 11.2) * 0.004);
    context!.fillStyle = "rgba(0,4,14,0.62)";
    context!.fillRect(0, 0, width, height);
    context!.restore();
    officialLogo(height * 0.24, unit * 0.2, alpha);
    centeredText("OMNISYNIA", height * 0.46, unit * 0.1, white, alpha);
    centeredText("CONECTA · SINCRONIZA · TRANSFORMA", height * 0.58, unit * 0.032, cyan, alpha, 600);
    centeredText(creatorLine, height * 0.75, unit * 0.019, muted, alpha * smooth(12, 12.8, time), 500);
  }

  let reportedProgress = -1;
  function drawFrame(time: number) {
    context!.clearRect(0, 0, width, height);
    if (time < 3.7) drawIntro(time);
    if (time >= 3.15 && time < 7.9) drawCampaign(time);
    if (time >= 7.25 && time < 11.85) drawCapabilities(time);
    if (time >= 11.2) drawFinal(time);
    const progress = Math.round(clamp(time / DURATION_SECONDS) * 100);
    context!.fillStyle = "rgba(255,255,255,0.1)";
    context!.fillRect(0, height - Math.max(8, unit * 0.004), width, Math.max(8, unit * 0.004));
    const bar = context!.createLinearGradient(0, 0, width, 0);
    bar.addColorStop(0, violet);
    bar.addColorStop(1, cyan);
    context!.fillStyle = bar;
    context!.fillRect(0, height - Math.max(8, unit * 0.004), width * progress / 100, Math.max(8, unit * 0.004));
    if (progress !== reportedProgress) {
      reportedProgress = progress;
      input.onProgress?.(progress);
    }
  }

  drawFrame(0);
  recorder.start(1_000);
  const startedAt = performance.now();
  await new Promise<void>((resolve) => {
    const render = () => {
      const seconds = (performance.now() - startedAt) / 1_000;
      drawFrame(Math.min(seconds, DURATION_SECONDS));
      if (seconds >= DURATION_SECONDS) resolve();
      else requestAnimationFrame(render);
    };
    requestAnimationFrame(render);
  });
  recorder.stop();
  const blob = await complete;
  stream.getTracks().forEach((track) => track.stop());
  if (audioContext) await audioContext.close();
  input.onProgress?.(100);

  if (blob.size === 0) throw new Error("El vídeo terminó vacío. Inténtalo de nuevo.");
  return {
    blob,
    mimeType: mediaType.mimeType,
    extension: mediaType.extension,
    width,
    height,
    durationSeconds: DURATION_SECONDS
  };
}
